import { useEffect, useState } from 'react';
import { Plus, Edit, Power, Trash2 } from 'lucide-react';
import { supabase } from '../services/supabase';
import { leerVarias, listaEnCastellano } from '../lib/leerVarias';
import Modal from '../components/ui/Modal';
import Confirm from '../components/ui/Confirm';

// PR-T1 — CRUD de códigos de descuento propios del club. La validación de
// negocio (combinaciones scope/tipo) la refuerza la BD con CHECKs (mig 124);
// aquí replicamos las reglas en el form para feedback inmediato.

interface DiscountCode {
  id: string;
  code: string;
  description: string | null;
  discount_type: string; // 'percentage' | 'fixed_amount'
  discount_value: number; // percentage: 0-100 · fixed_amount: céntimos
  scope: string; // 'item' | 'cart'
  target_product_id: string | null;
  target_session_pack_id: string | null;
  // mig 235 — productos que el carrito tiene que llevar TODOS. No confundir con
  // target_product_id: ese dice sobre qué línea cae el descuento; este, qué
  // tiene que haber comprado para que el código valga.
  requires_product_ids: string[] | null;
  valid_from: string | null;
  valid_until: string | null;
  max_uses_total: number | null;
  max_uses_per_user: number;
  new_users_only: boolean;
  allows_free_purchase: boolean;
  active: boolean;
}

interface TargetOption {
  id: string;
  name: string;
  kind: 'product' | 'pack';
}

interface DiscountForm {
  code: string;
  description: string;
  discount_type: string;
  // En el form el valor va en su unidad natural: percentage en %, fixed en €.
  value_input: string;
  scope: string;
  target: string; // '' | 'product:<id>' | 'pack:<id>'
  requires: string[]; // ids de producto que el carrito debe llevar (mig 235)
  valid_from: string;
  valid_until: string;
  max_uses_total: string;
  max_uses_per_user: number;
  new_users_only: boolean;
  allows_free_purchase: boolean;
  active: boolean;
}

const FORM_EMPTY: DiscountForm = {
  code: '',
  description: '',
  discount_type: 'percentage',
  value_input: '',
  scope: 'cart',
  target: '',
  requires: [],
  valid_from: '',
  valid_until: '',
  max_uses_total: '',
  max_uses_per_user: 1,
  new_users_only: false,
  allows_free_purchase: false,
  active: true,
};

function valueLabel(d: DiscountCode): string {
  return d.discount_type === 'percentage'
    ? `${d.discount_value}%`
    : `${(d.discount_value / 100).toFixed(2)} €`;
}

function dateRange(d: DiscountCode): string {
  const f = d.valid_from ? d.valid_from.slice(0, 10) : '—';
  const u = d.valid_until ? d.valid_until.slice(0, 10) : '—';
  return f === '—' && u === '—' ? 'Siempre' : `${f} → ${u}`;
}

// «0 USOS» ES UN DATO DE NEGOCIO: CON ÉL SE MATA O SE PRORROGA UNA PROMOCIÓN.
//
// La columna de usos pintaba `uses[d.id] ?? 0`, y ese `?? 0` no distinguía
// «nadie lo ha usado» de «no se ha podido contar». Un 0/100 al lado de un
// código vivo dice que la promoción no funciona, que es exactamente la
// conclusión que lleva a retirarla. Sin recuento se pinta «—».
const QUE = {
  codigos: 'los descuentos',
  productos: 'los productos',
  bonos: 'los bonos',
  usos: 'los usos de los códigos',
} as const;

export default function DescuentosPage() {
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [targets, setTargets] = useState<TargetOption[]>([]);
  const [uses, setUses] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [fallidos, setFallidos] = useState<string[]>([]);
  const [modal, setModal] = useState(false);
  const [editTarget, setEditTarget] = useState<DiscountCode | null>(null);
  const [form, setForm] = useState<DiscountForm>(FORM_EMPTY);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<DiscountCode | null>(null);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    const { datos, fallidos: noLeidos } = await leerVarias([
      {
        que: QUE.codigos,
        consulta: supabase
          .from('discount_codes')
          .select(
            'id, code, description, discount_type, discount_value, scope, target_product_id, target_session_pack_id, requires_product_ids, valid_from, valid_until, max_uses_total, max_uses_per_user, new_users_only, allows_free_purchase, active'
          )
          .order('created_at', { ascending: false }),
      },
      {
        que: QUE.productos,
        consulta: supabase.from('products').select('id, name').eq('active', true).order('name'),
      },
      {
        que: QUE.bonos,
        consulta: supabase
          .from('session_packs')
          .select('id, name')
          .eq('active', true)
          .order('name'),
      },
      { que: QUE.usos, consulta: supabase.from('discount_code_uses').select('code_id') },
    ]);
    const [codesData, prodData, packData, usesData] = datos;

    setFallidos(noLeidos);
    setCodes((codesData as DiscountCode[]) ?? []);

    const opts: TargetOption[] = [
      ...(((prodData as Array<{ id: string; name: string }>) ?? []).map((p) => ({
        id: p.id,
        name: `Producto · ${p.name}`,
        kind: 'product' as const,
      })) ?? []),
      ...(((packData as Array<{ id: string; name: string }>) ?? []).map((p) => ({
        id: p.id,
        name: `Bono · ${p.name}`,
        kind: 'pack' as const,
      })) ?? []),
    ];
    setTargets(opts);

    const tally: Record<string, number> = {};
    for (const u of (usesData as Array<{ code_id: string }>) ?? []) {
      tally[u.code_id] = (tally[u.code_id] ?? 0) + 1;
    }
    setUses(tally);
    setLoading(false);
  }

  function openCreate() {
    setEditTarget(null);
    setForm(FORM_EMPTY);
    setSaveError('');
    setModal(true);
  }

  function openEdit(d: DiscountCode) {
    setEditTarget(d);
    const target = d.target_product_id
      ? `product:${d.target_product_id}`
      : d.target_session_pack_id
        ? `pack:${d.target_session_pack_id}`
        : '';
    setForm({
      code: d.code,
      description: d.description ?? '',
      discount_type: d.discount_type,
      value_input:
        d.discount_type === 'percentage'
          ? String(d.discount_value)
          : (d.discount_value / 100).toFixed(2),
      scope: d.scope,
      requires: d.requires_product_ids ?? [],
      target,
      valid_from: d.valid_from ? d.valid_from.slice(0, 10) : '',
      valid_until: d.valid_until ? d.valid_until.slice(0, 10) : '',
      max_uses_total: d.max_uses_total != null ? String(d.max_uses_total) : '',
      max_uses_per_user: d.max_uses_per_user,
      new_users_only: d.new_users_only,
      allows_free_purchase: d.allows_free_purchase,
      active: d.active,
    });
    setSaveError('');
    setModal(true);
  }

  // Al cambiar a fixed_amount el scope debe ser 'item' (regla de negocio Q2).
  function setType(discount_type: string) {
    setForm((f) => ({
      ...f,
      discount_type,
      scope: discount_type === 'fixed_amount' ? 'item' : f.scope,
    }));
  }

  async function handleSave() {
    setSaveError('');
    const code = form.code.trim();
    if (!code) return setSaveError('El código es obligatorio.');

    const valueNum = Number(form.value_input);
    if (!(valueNum > 0)) return setSaveError('El valor del descuento debe ser mayor que 0.');
    if (form.discount_type === 'percentage' && valueNum > 100) {
      return setSaveError('Un descuento porcentual no puede superar el 100%.');
    }

    if (form.discount_type === 'fixed_amount' && form.scope !== 'item') {
      return setSaveError('Un descuento de importe fijo debe ser de ámbito «producto».');
    }

    // Espejo del CHECK `discount_free_purchase_is_full` (mig 193). Sin esto el
    // error llegaría como un 23514 crudo de Postgres.
    if (form.allows_free_purchase && !(form.discount_type === 'percentage' && valueNum === 100)) {
      return setSaveError(
        'Un código de compra gratuita tiene que ser un descuento porcentual del 100%.'
      );
    }

    let targetProductId: string | null = null;
    let targetSessionPackId: string | null = null;
    if (form.scope === 'item') {
      if (!form.target) return setSaveError('Selecciona el producto o bono al que aplica.');
      const [kind, id] = form.target.split(':');
      if (kind === 'product') targetProductId = id;
      else targetSessionPackId = id;
    }

    if (form.valid_from && form.valid_until && form.valid_until < form.valid_from) {
      return setSaveError('La fecha de fin no puede ser anterior a la de inicio.');
    }

    const maxTotal = form.max_uses_total.trim() === '' ? null : Number(form.max_uses_total);
    if (maxTotal != null && !(maxTotal > 0)) {
      return setSaveError(
        'El máximo de usos totales debe ser mayor que 0 (o vacío para ilimitado).'
      );
    }
    if (!(form.max_uses_per_user >= 1)) {
      return setSaveError('El máximo de usos por usuario debe ser al menos 1.');
    }

    const discountValue =
      form.discount_type === 'fixed_amount' ? Math.round(valueNum * 100) : valueNum;

    setSaving(true);
    const payload = {
      code,
      description: form.description.trim() || null,
      discount_type: form.discount_type,
      discount_value: discountValue,
      scope: form.scope,
      target_product_id: targetProductId,
      target_session_pack_id: targetSessionPackId,
      // Vacío se guarda como NULL, no como array vacío: un código sin condición
      // tiene que quedar indistinguible de los de antes de la mig 235.
      requires_product_ids: form.requires.length > 0 ? form.requires : null,
      valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
      valid_until: form.valid_until ? new Date(`${form.valid_until}T23:59:59`).toISOString() : null,
      max_uses_total: maxTotal,
      max_uses_per_user: form.max_uses_per_user,
      new_users_only: form.new_users_only,
      allows_free_purchase: form.allows_free_purchase,
      active: form.active,
    };
    const res = editTarget
      ? await supabase.from('discount_codes').update(payload).eq('id', editTarget.id)
      : await supabase.from('discount_codes').insert(payload);
    setSaving(false);
    if (res.error) {
      const dup = res.error.code === '23505' || /duplicate|unique/i.test(res.error.message);
      setSaveError(
        dup ? 'Ya existe un código con ese nombre.' : `No se pudo guardar: ${res.error.message}`
      );
      return;
    }
    await load();
    setModal(false);
  }

  async function toggle(d: DiscountCode) {
    const { error } = await supabase
      .from('discount_codes')
      .update({ active: !d.active })
      .eq('id', d.id);
    // El error se comprueba ANTES de tocar el estado: pintar la fila como
    // desactivada cuando la base ha dicho que no es peor que no pintar nada,
    // porque el cambio falso sobrevive hasta la siguiente recarga.
    if (error) {
      alert(`No se pudo ${d.active ? 'desactivar' : 'activar'}: ${error.message}`);
      return;
    }
    setCodes((prev) => prev.map((x) => (x.id === d.id ? { ...x, active: !x.active } : x)));
  }

  async function remove(d: DiscountCode) {
    const { error } = await supabase.from('discount_codes').delete().eq('id', d.id);
    if (error) {
      alert(`No se pudo eliminar: ${error.message}`);
      return;
    }
    setCodes((prev) => prev.filter((x) => x.id !== d.id));
    setConfirmDelete(null);
  }

  function targetName(d: DiscountCode): string {
    if (d.scope === 'cart') return 'Todo el carrito';
    const id = d.target_product_id ?? d.target_session_pack_id;
    return targets.find((t) => t.id === id)?.name ?? '—';
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-oswald text-2xl text-white uppercase tracking-wide">Descuentos</h1>
          <p className="text-text-secondary text-sm">
            Códigos propios del club. Se aplican en el checkout de la tienda unificada.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="btn-gold flex items-center gap-2"
          data-testid="descuento-new"
        >
          <Plus size={16} /> Nuevo descuento
        </button>
      </div>

      {fallidos.length > 0 && (
        <div
          className="text-error text-sm font-inter bg-error/10 border border-error/30 rounded-lg px-4 py-3"
          data-testid="descuentos-lecturas-fallidas"
        >
          No se han podido leer {listaEnCastellano(fallidos)}.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center h-48 items-center">
          <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
        </div>
      ) : codes.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-text-secondary text-sm" data-testid="descuentos-vacio">
            {fallidos.includes(QUE.codigos)
              ? 'No se han podido leer los descuentos.'
              : 'No hay descuentos todavía.'}
          </p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-text-secondary border-b border-bg-border">
                <th className="p-3 font-inter font-normal">Código</th>
                <th className="p-3 font-inter font-normal">Valor</th>
                <th className="p-3 font-inter font-normal">Ámbito</th>
                <th className="p-3 font-inter font-normal">Vigencia</th>
                <th className="p-3 font-inter font-normal w-20">Usos</th>
                <th className="p-3 font-inter font-normal w-24">Nuevos</th>
                <th className="p-3 font-inter font-normal w-24">Estado</th>
                <th className="p-3 font-inter font-normal w-28 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((d) => (
                <tr
                  key={d.id}
                  data-testid={`descuento-row-${d.id}`}
                  className={`border-b border-bg-border/60 ${!d.active ? 'opacity-50' : ''}`}
                >
                  <td className="p-3 text-white font-medium">
                    {d.code}
                    {d.allows_free_purchase && (
                      <span
                        data-testid={`descuento-free-${d.id}`}
                        className="ml-2 rounded bg-success/20 px-1.5 py-0.5 text-xs font-normal text-success align-middle"
                      >
                        Prueba gratuita
                      </span>
                    )}
                    {d.description && (
                      <span className="block text-text-secondary text-xs font-normal">
                        {d.description}
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-white">{valueLabel(d)}</td>
                  <td className="p-3 text-text-secondary">{targetName(d)}</td>
                  <td className="p-3 text-text-secondary text-xs">{dateRange(d)}</td>
                  <td className="p-3 text-text-secondary" data-testid={`descuento-usos-${d.code}`}>
                    {fallidos.includes(QUE.usos) ? '—' : (uses[d.id] ?? 0)}
                    {d.max_uses_total != null ? `/${d.max_uses_total}` : ''}
                  </td>
                  <td className="p-3 text-text-secondary">{d.new_users_only ? 'Solo' : '—'}</td>
                  <td className="p-3">
                    <span className={d.active ? 'text-success' : 'text-text-secondary'}>
                      {d.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => openEdit(d)}
                        title="Editar"
                        data-testid={`descuento-edit-${d.id}`}
                        className="w-7 h-7 rounded flex items-center justify-center text-text-secondary hover:text-gold-primary"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => toggle(d)}
                        title={d.active ? 'Desactivar' : 'Activar'}
                        className={`w-7 h-7 rounded flex items-center justify-center text-text-secondary ${d.active ? 'hover:text-warning' : 'hover:text-success'}`}
                      >
                        <Power size={14} />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(d)}
                        title="Eliminar"
                        data-testid={`descuento-delete-${d.id}`}
                        className="w-7 h-7 rounded flex items-center justify-center text-text-secondary hover:text-error"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Confirm
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && remove(confirmDelete)}
        title="Eliminar descuento"
        message={`¿Borrar "${confirmDelete?.code}" definitivamente? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        danger
      />

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={editTarget ? 'Editar descuento' : 'Nuevo descuento'}
        size="md"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Código *</label>
              <input
                className="input uppercase"
                data-testid="descuento-form-code"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="VERANO25"
              />
            </div>
            <div>
              <label className="label">Descripción</label>
              <input
                className="input"
                data-testid="descuento-form-description"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Campaña de verano"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Tipo</label>
              <select
                className="input"
                data-testid="descuento-form-type"
                value={form.discount_type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="percentage">Porcentaje (%)</option>
                <option value="fixed_amount">Importe fijo (€)</option>
              </select>
            </div>
            <div>
              <label className="label">
                {form.discount_type === 'percentage' ? 'Porcentaje (0-100)' : 'Importe (€)'}
              </label>
              <input
                type="number"
                step={form.discount_type === 'percentage' ? '1' : '0.01'}
                min="0"
                className="input"
                data-testid="descuento-form-value"
                value={form.value_input}
                onChange={(e) => setForm((f) => ({ ...f, value_input: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Ámbito</label>
              <select
                className="input"
                data-testid="descuento-form-scope"
                value={form.scope}
                disabled={form.discount_type === 'fixed_amount'}
                onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value }))}
              >
                <option value="cart">Todo el carrito</option>
                <option value="item">Producto / bono concreto</option>
              </select>
              {form.discount_type === 'fixed_amount' && (
                <p className="text-text-secondary text-xs mt-1">
                  El importe fijo solo aplica a un producto/bono concreto.
                </p>
              )}
            </div>
            {form.scope === 'item' && (
              <div>
                <label className="label">Producto / bono *</label>
                <select
                  className="input"
                  data-testid="descuento-form-target"
                  value={form.target}
                  onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))}
                >
                  <option value="">— Selecciona —</option>
                  {targets.map((t) => (
                    <option key={`${t.kind}:${t.id}`} value={`${t.kind}:${t.id}`}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* mig 235 — la condición del carrito.
              Sin esto, un código de 55 € sobre la suscripción se lo llevaba
              cualquiera que añadiese algo barato al carrito: medido, la
              suscripción de 60 € salía por 20 €. Y quien no cumpla la condición
              no se queda a medias en silencio — el pago se corta y le dice qué
              le falta. */}
          <div>
            <label className="label">Exige llevar en el carrito</label>
            <p className="text-text-secondary text-xs mb-2">
              El código solo vale si el carrito lleva <strong>todos</strong> estos productos. Déjalo
              vacío para que no exija nada.
            </p>
            <div
              className="max-h-40 overflow-y-auto rounded border border-white/10 p-2 space-y-1"
              data-testid="descuento-form-requires"
            >
              {targets.filter((t) => t.kind === 'product').length === 0 ? (
                <p className="text-text-secondary text-xs" data-testid="descuentos-catalogo-vacio">
                  {fallidos.includes(QUE.productos)
                    ? 'No se ha podido leer el catálogo.'
                    : 'No hay productos en el catálogo.'}
                </p>
              ) : (
                targets
                  .filter((t) => t.kind === 'product')
                  .map((t) => (
                    <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`descuento-form-requires-${t.id}`}
                        checked={form.requires.includes(t.id)}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            requires: e.target.checked
                              ? [...f.requires, t.id]
                              : f.requires.filter((x) => x !== t.id),
                          }))
                        }
                      />
                      <span>{t.name}</span>
                    </label>
                  ))
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Válido desde</label>
              <input
                type="date"
                className="input"
                data-testid="descuento-form-valid-from"
                value={form.valid_from}
                onChange={(e) => setForm((f) => ({ ...f, valid_from: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Válido hasta</label>
              <input
                type="date"
                className="input"
                data-testid="descuento-form-valid-until"
                value={form.valid_until}
                onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Máx. usos totales (vacío = ∞)</label>
              <input
                type="number"
                min="1"
                className="input"
                data-testid="descuento-form-max-total"
                value={form.max_uses_total}
                onChange={(e) => setForm((f) => ({ ...f, max_uses_total: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Máx. usos por usuario</label>
              <input
                type="number"
                min="1"
                className="input"
                data-testid="descuento-form-max-user"
                value={form.max_uses_per_user}
                onChange={(e) => setForm((f) => ({ ...f, max_uses_per_user: +e.target.value }))}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
              <input
                type="checkbox"
                data-testid="descuento-form-new-users"
                checked={form.new_users_only}
                onChange={(e) => setForm((f) => ({ ...f, new_users_only: e.target.checked }))}
              />
              Solo alumnos nuevos (sin compras pagadas)
            </label>
            {/* Mig 193 — la marca que convierte un 100% en una sesión de prueba
                canjeable sin pagar y sin matrícula. Sin marcar, un 100% deja el
                total a 0 y la compra se rechaza. */}
            <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
              <input
                type="checkbox"
                data-testid="descuento-form-free-purchase"
                checked={form.allows_free_purchase}
                onChange={(e) => setForm((f) => ({ ...f, allows_free_purchase: e.target.checked }))}
              />
              Sesión de prueba gratuita (canjeable sin pagar y sin matrícula)
            </label>
            {form.allows_free_purchase && (
              <p className="text-xs text-text-secondary pl-6">
                Debe ser un 100%. Solo se canjea en carritos de un bono de 1 sesión, grupal o
                individual — nunca GYM. El crédito dura 30 días.
              </p>
            )}
            <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
              <input
                type="checkbox"
                data-testid="descuento-form-active"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              />
              Activo
            </label>
          </div>

          {saveError && (
            <p
              className="text-error text-xs bg-error/10 rounded p-2"
              data-testid="descuento-save-error"
            >
              {saveError}
            </p>
          )}

          <div className="flex justify-end gap-3">
            <button onClick={() => setModal(false)} className="btn-ghost">
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-gold"
              data-testid="descuento-save"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
